import { Box, makeStyles, Typography, Button } from "@material-ui/core";
import { Link } from "@material-ui/core";
import ArrowBackIosIcon from "@material-ui/icons/ArrowBackIos";
import NoteAddIcon from "@material-ui/icons/NoteAdd";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  withStyles,
  createStyles,
} from "@material-ui/core";
import { observer } from "mobx-react";
import { useStore } from "@roc/client-portal-shared/hooks";
import { propertyTypeOptions, viewOptions } from "@roc/feature-valuation-review-v2";
import { ComparableProperty } from "../types";
import { createNumberFormatField, createRadioField, createSelectField, createTextField, TextField, createPercentageField, createNumberFormatFieldWithAdornment, createDateField, createCurrencyField, createCheckboxField } from "@roc/ui";
import { PropertyCompFormStore } from "../stores/propertyCompFormStore";
import { AddressPart, getAddressPart } from "../utils/utils";
import PropertyPhotosGallery from "./propertyPhotosGallery";
import { ReactComponent as HouseWithPlusFilledIcon } from '../images/housewithplusfilled.svg';
import { createNumberFormatFieldNoDecimalsAndWithLimit } from "@roc/feature-one-tool";
import { useMemo, useState } from "react";
import { capitalizeOnlyFirstLetter } from "@roc/feature-comp-tool";
import { ComparablesFeedbackModal } from "./comparablesFeedbackModal";

const useStyles = makeStyles((theme) => ({
  root: {
    backgroundColor: "#FFFFFF",
    padding: "2rem",
    width: "100%"
  },
  backLink: {
    display: "flex",
    alignItems: "center",
    cursor: "pointer",
    color: "theme.palette.primary.main",
    textDecoration: "none",
    fontWeight: "bold",
    fontSize: "1rem",
    "&:hover": {
      textDecoration: "underline",
    },
  },
  button: {
    paddingBlock: "0px !important",
    height: "40px",
  },
  fontSize: {
    fontSize: "0.9rem",
  },
  scrollContainer: {
    overflowX: "auto",
    minWidth: "700px"
  },
  noBorder: {
    border: "none"
  },
  subjectPropertyTableContainer: {
    position: "relative",
    zIndex: 2,
    boxShadow: "18px 0 15px -11px rgba(0, 0, 0, 0.2)",
  },
  compsTableContainer: {
    position: "relative",
    zIndex: 1,
    overflowX: "auto",
  },
  radioContainer: {
    "& .MuiFormControl-fullWidth": {
      width: "auto !important"
    }
  },
  headerTitle: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center"
  },
  icon: {
    width: "20px",
    height: "20px",
    marginLeft: "16px",
    color: theme.palette.primary.main,

  },
  fieldContainer: {
    "& .MuiFormControlLabel-root": {
      margin: "0 !important"
    }
  }
}));

interface CompareSelectionProps {
  onBack: () => void;
}

const CompareSelection = observer(({ onBack }: CompareSelectionProps) => {
  const classes = useStyles();
  const { compToolStoreV2: compToolStore } = useStore();
  const [openComparablesFeedbackModal, setOpenComparablesFeedbackModal] = useState(false)
  const selectedIds = new Set(compToolStore.selectedCompareProperties.map(c => c.property_id));
  const { selectedCompareProperties: propertyComps, subjectProperty, propertyCompFormStores } = compToolStore;

  const selectedPropertiesCount = compToolStore.selectedCompareProperties?.length || 0;

  //this is done to go back to previous page if the user
  //removes all the selected comps
  if (selectedPropertiesCount === 0) {
    onBack();
  }

  const subjectPropertyFormStore: PropertyCompFormStore = propertyCompFormStores.find(store => store.form.fields.propertyId.value === subjectProperty.property_id);
  const compFormStores: PropertyCompFormStore[] = selectedPropertiesCount > 0 ? propertyCompFormStores.filter(store => store.form.fields.propertyId.value !== subjectProperty.property_id) : [];

  const loanSelected = !!compToolStore.loanInformation?.loan_id;


  const handleAddComparablesToLoan = async () => {
    const response = await compToolStore.fetchFirstThreeProperties()

    if (!response) {
      compToolStore.savePropertyComps()
    } else {
      const comparablesFeedbackIds = compToolStore.firstThreeProperties
        .map((p) => p.property_id)

      const thereIsComparablesNotSelected = comparablesFeedbackIds.find(propertyId => !selectedIds.has(propertyId))

      if (thereIsComparablesNotSelected)
        setOpenComparablesFeedbackModal(true)
      else
        compToolStore.savePropertyComps()
    }

  }

  const handleOnSaveComplete = () => {
    compToolStore.savePropertyComps()
    setOpenComparablesFeedbackModal(false)
  }

  return (
    <Box
      display="flex"
      flexDirection="column"
      gridGap={16}
      className={classes.root}
    >
      <Typography variant="h4"> Comparable Analytics Tool</Typography>
      <Box
        display="flex"
        flexDirection="row"
        alignItems="center"
        justifyContent="space-between"
      >
        <Box display="flex" justifyContent="space-between" width="100%">
          <Link
            component="button"
            className={classes.backLink}
            onClick={onBack}
          >
            <ArrowBackIosIcon />
            Back to Search Results
          </Link>
          {loanSelected && (
            <Button
              className={classes.button}
              startIcon={<NoteAddIcon />}
              variant="outlined"
              color="primary"
              onClick={handleAddComparablesToLoan}
            >
              ADD COMPARABLES TO LOAN
            </Button>
          )}

        </Box>
      </Box>
      <Box display="flex" flexDirection="column" width="100%">
        <Box display="flex" width="100%">
          <Box className={classes.subjectPropertyTableContainer}>
            <TableContainer>
              <Table style={{ tableLayout: "fixed", width: "0" }}>
                <TableHead>
                  <StyledTableRow key="header-row">
                    <StyledTableCell withBorder
                      className={classes.fontSize}
                    ></StyledTableCell>
                    <SubjectPropertyCell withBorder className={classes.fontSize}>
                      <PropertyHeader title="Subject Property" property={subjectProperty} />
                    </SubjectPropertyCell>
                  </StyledTableRow>
                </TableHead>
                <TableBody>
                  <StyledTableRow>
                    <StyledTableCell withBorder>Address</StyledTableCell>
                    <SubjectPropertyCell withBorder>
                      {getAddressPart(subjectProperty.data.address, AddressPart.ADDRESS)}
                    </SubjectPropertyCell>
                  </StyledTableRow>
                  <StyledTableRow>
                    <StyledTableCell withBorder>City, State, Zip</StyledTableCell>
                    <SubjectPropertyCell withBorder>
                      {getAddressPart(subjectProperty.data.address, AddressPart.ZIP)}
                    </SubjectPropertyCell>
                  </StyledTableRow>
                  <StyledTableRow>
                    <StyledTableCell withBorder>MLS #</StyledTableCell>
                    <SubjectPropertyCell withBorder>
                      {createTextField({
                        label: '',
                        fieldName: 'mlsNumber',
                        testId: 'mlsNumber-' + subjectProperty.property_id,
                        store: subjectPropertyFormStore,
                        standaloneLabel: false,
                      })}
                    </SubjectPropertyCell>
                  </StyledTableRow>
                  <StyledTableRow>
                    <StyledTableCell withBorder>Proximity</StyledTableCell>
                    <SubjectPropertyCell withBorder>
                    </SubjectPropertyCell>
                  </StyledTableRow>
                  <StyledTableRow>
                    <StyledTableCell withBorder>Original List Date</StyledTableCell>
                    <SubjectPropertyCell withBorder>
                      {createDateField({
                        label: '',
                        fieldName: 'originalListDate',
                        testId: 'originalListDate-' + subjectProperty.property_id,
                        store: subjectPropertyFormStore,
                        standaloneLabel: false,
                      })}
                    </SubjectPropertyCell>
                  </StyledTableRow>
                  <StyledTableRow>
                    <StyledTableCell withBorder>Original List Price</StyledTableCell>
                    <SubjectPropertyCell withBorder>
                      {createCurrencyField({
                        label: '',
                        fieldName: 'originalListPrice',
                        testId: 'originalListPrice' + subjectProperty.property_id,
                        store: subjectPropertyFormStore,
                        standaloneLabel: false,
                      })}
                    </SubjectPropertyCell>
                  </StyledTableRow>
                  <StyledTableRow>
                    <StyledTableCell withBorder>DOM</StyledTableCell>
                    <SubjectPropertyCell withBorder>
                      {createNumberFormatFieldNoDecimalsAndWithLimit({
                        label: '',
                        fieldName: 'domTrend',
                        testId: 'domTrend' + subjectProperty.property_id,
                        store: subjectPropertyFormStore,
                        standaloneLabel: false,
                        allowNegative: false
                      })}
                    </SubjectPropertyCell>
                  </StyledTableRow>
                  <StyledTableRow>
                    <StyledTableCell withBorder>Property Type</StyledTableCell>
                    <SubjectPropertyCell withBorder>
                      <Box style={{ textAlign: "left" }}>
                        {createSelectField({
                          store: subjectPropertyFormStore,
                          testId: 'propertyType-' + subjectProperty.property_id,
                          fieldName: 'propertyType',
                          label: '',
                          options: getPropertyTypeOptions(subjectProperty?.data?.property_type),
                          standaloneLabel: true
                        })}
                      </Box>
                    </SubjectPropertyCell>
                  </StyledTableRow>
                  <StyledTableRow>
                    <StyledTableCell withBorder>Sale Type</StyledTableCell>
                    <SubjectPropertyCell withBorder>
                      {createTextField({
                        label: '',
                        fieldName: 'saleType',
                        testId: 'saleType-' + subjectProperty.property_id,
                        store: subjectPropertyFormStore,
                        standaloneLabel: false,
                      })}
                    </SubjectPropertyCell>
                  </StyledTableRow>
                  <StyledTableRow>
                    <StyledTableCell withBorder>Lot Size</StyledTableCell>
                    <SubjectPropertyCell withBorder>
                      {createNumberFormatField({
                        label: '',
                        fieldName: 'lotSize',
                        testId: 'lotSize-' + subjectProperty.property_id,
                        store: subjectPropertyFormStore,
                        standaloneLabel: false,
                        allowNegative: false
                      })}
                    </SubjectPropertyCell>
                  </StyledTableRow>
                  <StyledTableRow>
                    <StyledTableCell withBorder>Year Built</StyledTableCell>
                    <SubjectPropertyCell withBorder>
                      {createNumberFormatFieldNoDecimalsAndWithLimit({
                        label: '',
                        fieldName: 'yearBuilt',
                        testId: 'yearBuilt-' + subjectProperty.property_id,
                        store: subjectPropertyFormStore,
                        standaloneLabel: false,
                        allowNegative: false
                      })}
                    </SubjectPropertyCell>
                  </StyledTableRow>
                  <StyledTableRow>
                    <StyledTableCell withBorder>{compToolStore.rentalComps ? "Rent" : "Price"}</StyledTableCell>
                    <SubjectPropertyCell withBorder>
                      {createCurrencyField({
                        label: '',
                        fieldName: 'priceRent',
                        testId: 'priceRent' + subjectProperty.property_id,
                        store: subjectPropertyFormStore,
                        standaloneLabel: false,
                      })}
                    </SubjectPropertyCell>
                  </StyledTableRow>
                  <StyledTableRow>
                    <StyledTableCell withBorder>Last Sold</StyledTableCell>
                    <SubjectPropertyCell withBorder>
                      {createDateField({
                        label: '',
                        fieldName: 'saleDate',
                        testId: 'saleDate-' + subjectProperty.property_id,
                        store: subjectPropertyFormStore,
                        standaloneLabel: false,
                      })}
                    </SubjectPropertyCell>
                  </StyledTableRow>
                  <StyledTableRow>
                    <StyledTableCell withBorder>Rooms</StyledTableCell>
                    <SubjectPropertyCell withBorder>
                      {createNumberFormatFieldNoDecimalsAndWithLimit({
                        label: '',
                        fieldName: 'rooms',
                        testId: 'rooms-' + subjectProperty.property_id,
                        store: subjectPropertyFormStore,
                        standaloneLabel: false,
                        allowNegative: false
                      })}
                    </SubjectPropertyCell>
                  </StyledTableRow>
                  <StyledTableRow>
                    <StyledTableCell withBorder>Bedrooms</StyledTableCell>
                    <SubjectPropertyCell withBorder>
                      {createNumberFormatFieldNoDecimalsAndWithLimit({
                        label: '',
                        fieldName: 'bedrooms',
                        testId: 'bedrooms-' + subjectProperty.property_id,
                        store: subjectPropertyFormStore,
                        standaloneLabel: false,
                        allowNegative: false
                      })}
                    </SubjectPropertyCell>
                  </StyledTableRow>
                  <StyledTableRow>
                    <StyledTableCell withBorder>Baths</StyledTableCell>
                    <SubjectPropertyCell withBorder>
                      {createNumberFormatFieldNoDecimalsAndWithLimit({
                        label: '',
                        fieldName: 'baths',
                        testId: 'baths-' + subjectProperty.property_id,
                        store: subjectPropertyFormStore,
                        standaloneLabel: false,
                        allowNegative: false
                      })}
                    </SubjectPropertyCell>
                  </StyledTableRow>
                  <StyledTableRow>
                    <StyledTableCell withBorder>Half Baths</StyledTableCell>
                    <SubjectPropertyCell withBorder>
                      {createNumberFormatFieldNoDecimalsAndWithLimit({
                        label: '',
                        fieldName: 'halfBaths',
                        testId: 'halfBaths-' + subjectProperty.property_id,
                        store: subjectPropertyFormStore,
                        standaloneLabel: false,
                        allowNegative: false
                      })}
                    </SubjectPropertyCell>
                  </StyledTableRow>
                  <StyledTableRow>
                    <StyledTableCell withBorder>Sq.Ft.</StyledTableCell>
                    <SubjectPropertyCell withBorder>
                      {createNumberFormatField({
                        label: '',
                        fieldName: 'area',
                        testId: 'area-' + subjectProperty.property_id,
                        store: subjectPropertyFormStore,
                        standaloneLabel: false,
                        allowNegative: false
                      })}
                    </SubjectPropertyCell>
                  </StyledTableRow>
                  <StyledTableRow>
                    <StyledTableCell withBorder>Price/Sq.Ft.</StyledTableCell>
                    <SubjectPropertyCell withBorder>
                      {createCurrencyField({
                        label: '',
                        fieldName: 'pricePerSqFt',
                        testId: 'pricePerSqFt' + subjectProperty.property_id,
                        store: subjectPropertyFormStore,
                        standaloneLabel: false,
                      })}
                    </SubjectPropertyCell>
                  </StyledTableRow>
                  <StyledTableRow>
                    <StyledTableCell withBorder>Taxes/Year</StyledTableCell>
                    <SubjectPropertyCell withBorder>
                      {createCurrencyField({
                        label: '',
                        fieldName: 'taxes',
                        testId: 'taxes' + subjectProperty.property_id,
                        store: subjectPropertyFormStore,
                        standaloneLabel: false,
                      })}
                    </SubjectPropertyCell>
                  </StyledTableRow>
                  <StyledTableRow>
                    <StyledTableCell withBorder>Parking Type</StyledTableCell>
                    <SubjectPropertyCell withBorder>
                      {createTextField({
                        label: '',
                        fieldName: 'parkingType',
                        testId: 'parkingType-' + subjectProperty.property_id,
                        store: subjectPropertyFormStore,
                        standaloneLabel: false,
                      })}
                    </SubjectPropertyCell>
                  </StyledTableRow>
                  <StyledTableRow>
                    <StyledTableCell withBorder>Amenities</StyledTableCell>
                    <SubjectPropertyCell withBorder>
                      {createTextField({
                        label: '',
                        fieldName: 'amenities',
                        testId: 'amenities-' + subjectProperty.property_id,
                        store: subjectPropertyFormStore,
                        standaloneLabel: false,
                      })}
                    </SubjectPropertyCell>
                  </StyledTableRow>
                  <StyledTableRow>
                    <StyledTableCell withBorder>Pool</StyledTableCell>
                    <SubjectPropertyCell withBorder>
                      {createCheckboxField({
                        label: '',
                        fieldName: 'hasPool',
                        store: subjectPropertyFormStore,
                        color: "primary"
                      })}
                    </SubjectPropertyCell>
                  </StyledTableRow>
                  {loanSelected && (
                    <>
                      <StyledTableRow>
                        <StyledTableCell colSpan={2}>
                        </StyledTableCell>
                      </StyledTableRow>
                      <StyledTableRow>
                        <StyledTableCell className={classes.noBorder}>Best Comparable</StyledTableCell>
                        <SubjectPropertyCell className={classes.noBorder}>
                          <div style={{ height: "45px" }}>{/**Placeholder */}</div>
                        </SubjectPropertyCell>
                      </StyledTableRow>
                      <StyledTableRow>
                        <StyledTableCell className={classes.noBorder}>Weight Adjustment</StyledTableCell>
                        <SubjectPropertyCell className={classes.noBorder}>
                          <div style={{ height: "45px" }}>{/**Placeholder */}</div>
                        </SubjectPropertyCell>
                      </StyledTableRow>
                      <StyledTableRow>
                        <StyledTableCell className={classes.noBorder}>No. of units</StyledTableCell>
                        <SubjectPropertyCell className={classes.noBorder}>
                          {createNumberFormatField({
                            label: '',
                            fieldName: 'noOfUnits',
                            testId: 'noOfUnits' + subjectPropertyFormStore.form.fields.propertyId.value,
                            store: subjectPropertyFormStore,
                            standaloneLabel: false,
                          })}
                        </SubjectPropertyCell>
                      </StyledTableRow>
                      <StyledTableRow>
                        <StyledTableCell className={classes.noBorder}>View</StyledTableCell>
                        <SubjectPropertyCell className={classes.noBorder}>
                          {createTextField({
                            label: '',
                            fieldName: 'view',
                            testId: 'view' + subjectPropertyFormStore.form.fields.propertyId.value,
                            store: subjectPropertyFormStore,
                            standaloneLabel: false,
                          })}
                        </SubjectPropertyCell>
                      </StyledTableRow>
                      <StyledTableRow>
                        <StyledTableCell className={classes.noBorder}>Comparability</StyledTableCell>
                        <SubjectPropertyCell className={classes.noBorder}>
                          {createTextField({
                            label: '',
                            fieldName: 'comparability',
                            testId: 'comparability' + subjectPropertyFormStore.form.fields.propertyId.value,
                            store: subjectPropertyFormStore,
                            standaloneLabel: false,
                          })}
                        </SubjectPropertyCell>
                      </StyledTableRow>
                    </>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
          <Box flexGrow={1} className={classes.compsTableContainer}>
            <TableContainer>
              <Table style={{ tableLayout: "fixed", width: "0" }}>
                <TableHead>
                  <StyledTableRow key="header-row">
                    {propertyComps.map((comp, index) => (
                      <CompPropertyCell withBorder className={classes.fontSize}>
                        <PropertyHeader title={`Comparable #${index + 1}`} property={comp} onRemove={() => compToolStore.removePropertyFromCompareTool(comp.property_id)} />
                      </CompPropertyCell>
                    ))}
                  </StyledTableRow>
                </TableHead>
                <TableBody>
                  <StyledTableRow>
                    {propertyComps.map((comp) => (
                      <CompPropertyCell withBorder key={`address-${comp.property_id}`}>
                        {getAddressPart(comp.data.address, AddressPart.ADDRESS)}
                      </CompPropertyCell>
                    ))}
                  </StyledTableRow>
                  <StyledTableRow>
                    {propertyComps.map((comp) => (
                      <CompPropertyCell withBorder key={`citi-state-zip-${comp.property_id}`}>
                        {getAddressPart(comp.data.address, AddressPart.CITY_STATE_ZIP)}
                      </CompPropertyCell>
                    ))}
                  </StyledTableRow>
                  <StyledTableRow>
                    {propertyComps.map((comp, index) => (
                      <CompPropertyCell withBorder key={`mls-id-${comp.property_id}`}>
                        {createTextField({
                          label: '',
                          fieldName: 'mlsNumber',
                          testId: 'mlsNumber-' + comp.property_id,
                          store: compFormStores[index],
                          standaloneLabel: false,
                        })}
                      </CompPropertyCell>
                    ))}
                  </StyledTableRow>
                  <StyledTableRow>
                    {propertyComps.map((comp, index) => (
                      <CompPropertyCell withBorder key={`proximity-${comp.property_id}`}>
                        {createNumberFormatFieldWithAdornment({
                          label: '',
                          fieldName: 'proximity',
                          testId: 'proximity-' + comp.property_id,
                          store: compFormStores[index],
                          standaloneLabel: false,
                          adornment: "mi",
                          adornmentPosition: "end"
                        })}
                      </CompPropertyCell>
                    ))}
                  </StyledTableRow>
                  <StyledTableRow>
                    {propertyComps.map((comp, index) => (
                      <CompPropertyCell withBorder key={`original-list-date-${comp.property_id}`}>
                        {createDateField({
                          label: '',
                          fieldName: 'originalListDate',
                          testId: 'originalListDate-' + comp.property_id,
                          store: compFormStores[index],
                          standaloneLabel: false,
                        })}
                      </CompPropertyCell>
                    ))}
                  </StyledTableRow>
                  <StyledTableRow>
                    {propertyComps.map((comp, index) => (
                      <CompPropertyCell withBorder key={`original-list-price-${comp.property_id}`}>
                        {createCurrencyField({
                          label: '',
                          fieldName: 'originalListPrice',
                          testId: 'originalListPrice' + comp.property_id,
                          store: compFormStores[index],
                          standaloneLabel: false,
                        })}
                      </CompPropertyCell>
                    ))}
                  </StyledTableRow>
                  <StyledTableRow>
                    {propertyComps.map((comp, index) => (
                      <CompPropertyCell withBorder key={`domTrend-${comp.property_id}`}>
                        {createNumberFormatFieldNoDecimalsAndWithLimit({
                          label: '',
                          fieldName: 'domTrend',
                          testId: 'domTrend' + comp.property_id,
                          store: compFormStores[index],
                          standaloneLabel: false,
                          allowNegative: false
                        })}
                      </CompPropertyCell>
                    ))}
                  </StyledTableRow>
                  <StyledTableRow>
                    {propertyComps.map((comp, index) => (
                      <CompPropertyCell withBorder key={`propertyType-${comp.property_id}`}>
                        <Box style={{ textAlign: "left" }}>
                          {createSelectField({
                            store: compFormStores[index],
                            testId: 'propertyType-' + comp.property_id,
                            fieldName: 'propertyType',
                            label: '',
                            options: getPropertyTypeOptions(comp?.data?.property_type),
                            standaloneLabel: true
                          })}
                        </Box>
                      </CompPropertyCell>
                    ))}
                  </StyledTableRow>
                  <StyledTableRow>
                    {propertyComps.map((comp, index) => (
                      <CompPropertyCell withBorder key={`saleType-${comp.property_id}`}>
                        {createTextField({
                          label: '',
                          fieldName: 'saleType',
                          testId: 'saleType-' + comp.property_id,
                          store: compFormStores[index],
                          standaloneLabel: false,
                        })}
                      </CompPropertyCell>
                    ))}
                  </StyledTableRow>
                  <StyledTableRow>
                    {propertyComps.map((comp, index) => (
                      <CompPropertyCell withBorder key={`lotSize-${comp.property_id}`}>
                        {createNumberFormatField({
                          label: '',
                          fieldName: 'lotSize',
                          testId: 'lotSize-' + comp.property_id,
                          store: compFormStores[index],
                          standaloneLabel: false,
                          allowNegative: false
                        })}
                      </CompPropertyCell>
                    ))}
                  </StyledTableRow>
                  <StyledTableRow>
                    {propertyComps.map((comp, index) => (
                      <CompPropertyCell withBorder key={`yearBuilt-${comp.property_id}`}>
                        {createNumberFormatFieldNoDecimalsAndWithLimit({
                          label: '',
                          fieldName: 'yearBuilt',
                          testId: 'yearBuilt-' + comp.property_id,
                          store: compFormStores[index],
                          standaloneLabel: false,
                          allowNegative: false
                        })}
                      </CompPropertyCell>
                    ))}
                  </StyledTableRow>
                  <StyledTableRow>
                    {propertyComps.map((comp, index) => (
                      <CompPropertyCell withBorder key={`priceRent-${comp.property_id}`}>
                        {createCurrencyField({
                          label: '',
                          fieldName: 'priceRent',
                          testId: 'priceRent' + comp.property_id,
                          store: compFormStores[index],
                          standaloneLabel: false,
                        })}
                      </CompPropertyCell>
                    ))}
                  </StyledTableRow>
                  <StyledTableRow>
                    {propertyComps.map((comp, index) => (
                      <CompPropertyCell withBorder key={`saleDate-${comp.property_id}`}>
                        {createDateField({
                          label: '',
                          fieldName: 'saleDate',
                          testId: 'saleDate-' + comp.property_id,
                          store: compFormStores[index],
                          standaloneLabel: false,
                        })}
                      </CompPropertyCell>
                    ))}
                  </StyledTableRow>
                  <StyledTableRow>
                    {propertyComps.map((comp, index) => (
                      <CompPropertyCell withBorder key={`rooms-${comp.property_id}`}>
                        {createNumberFormatFieldNoDecimalsAndWithLimit({
                          label: '',
                          fieldName: 'rooms',
                          testId: 'rooms-' + comp.property_id,
                          store: compFormStores[index],
                          standaloneLabel: false,
                          allowNegative: false
                        })}
                      </CompPropertyCell>
                    ))}
                  </StyledTableRow>
                  <StyledTableRow>
                    {propertyComps.map((comp, index) => (
                      <CompPropertyCell withBorder key={`bedrooms-${comp.property_id}`}>
                        {createNumberFormatFieldNoDecimalsAndWithLimit({
                          label: '',
                          fieldName: 'bedrooms',
                          testId: 'bedrooms-' + comp.property_id,
                          store: compFormStores[index],
                          standaloneLabel: false,
                          allowNegative: false
                        })}
                      </CompPropertyCell>
                    ))}
                  </StyledTableRow>
                  <StyledTableRow>
                    {propertyComps.map((comp, index) => (
                      <CompPropertyCell withBorder key={`baths-${comp.property_id}`}>
                        {createNumberFormatFieldNoDecimalsAndWithLimit({
                          label: '',
                          fieldName: 'baths',
                          testId: 'baths-' + comp.property_id,
                          store: compFormStores[index],
                          standaloneLabel: false,
                          allowNegative: false
                        })}
                      </CompPropertyCell>
                    ))}
                  </StyledTableRow>
                  <StyledTableRow>
                    {propertyComps.map((comp, index) => (
                      <CompPropertyCell withBorder key={`halfBaths-${comp.property_id}`}>
                        {createNumberFormatFieldNoDecimalsAndWithLimit({
                          label: '',
                          fieldName: 'halfBaths',
                          testId: 'halfBaths-' + comp.property_id,
                          store: compFormStores[index],
                          standaloneLabel: false,
                          allowNegative: false
                        })}
                      </CompPropertyCell>
                    ))}
                  </StyledTableRow>
                  <StyledTableRow>
                    {propertyComps.map((comp, index) => (
                      <CompPropertyCell withBorder key={`area-${comp.property_id}`}>
                        {createNumberFormatField({
                          label: '',
                          fieldName: 'area',
                          testId: 'area-' + comp.property_id,
                          store: compFormStores[index],
                          standaloneLabel: false,
                          allowNegative: false
                        })}
                      </CompPropertyCell>
                    ))}
                  </StyledTableRow>
                  <StyledTableRow>
                    {propertyComps.map((comp, index) => (
                      <CompPropertyCell withBorder key={`pricePerSqFt-${comp.property_id}`}>
                        {createCurrencyField({
                          label: '',
                          fieldName: 'pricePerSqFt',
                          testId: 'pricePerSqFt' + comp.property_id,
                          store: compFormStores[index],
                          standaloneLabel: false,
                        })}
                      </CompPropertyCell>
                    ))}
                  </StyledTableRow>
                  <StyledTableRow>
                    {propertyComps.map((comp, index) => (
                      <CompPropertyCell withBorder key={`taxes-${comp.property_id}`}>
                        {createCurrencyField({
                          label: '',
                          fieldName: 'taxes',
                          testId: 'taxes' + comp.property_id,
                          store: compFormStores[index],
                          standaloneLabel: false,
                        })}
                      </CompPropertyCell>
                    ))}
                  </StyledTableRow>
                  <StyledTableRow>
                    {propertyComps.map((comp, index) => (
                      <CompPropertyCell withBorder key={`parkingType-${comp.property_id}`}>
                        {createTextField({
                          label: '',
                          fieldName: 'parkingType',
                          testId: 'parkingType-' + comp.property_id,
                          store: compFormStores[index],
                          standaloneLabel: false,
                        })}
                      </CompPropertyCell>
                    ))}
                  </StyledTableRow>
                  <StyledTableRow>
                    {propertyComps.map((comp, index) => (
                      <CompPropertyCell withBorder key={`amenities-${comp.property_id}`}>
                        {createTextField({
                          label: '',
                          fieldName: 'amenities',
                          testId: 'amenities-' + comp.property_id,
                          store: compFormStores[index],
                          standaloneLabel: false,
                        })}
                      </CompPropertyCell>
                    ))}
                  </StyledTableRow>
                  <StyledTableRow>
                    {propertyComps.map((comp, index) => (
                      <CompPropertyCell withBorder key={`hasPool-${comp.property_id}`}>
                        <Box className={classes.fieldContainer}>
                          {createCheckboxField({
                            label: '',
                            fieldName: 'hasPool',
                            store: compFormStores[index],
                            color: "primary"
                          })}
                        </Box>
                      </CompPropertyCell>
                    ))}
                  </StyledTableRow>
                  {loanSelected && (<>
                    <StyledTableRow>
                      {propertyComps.map(() => (
                        <StyledTableCell>
                        </StyledTableCell>
                      ))}
                    </StyledTableRow>
                    <StyledTableRow>
                      {propertyComps.map((comp, index) => (
                        <CompPropertyCell key={`best-comparable-cell-${comp.property_id}`} className={classes.noBorder}>
                          <Box className={classes.radioContainer}>

                            {createRadioField({
                              store: compFormStores[index],
                              testId: 'bestComparable' + comp.property_id,
                              fieldName: 'bestComparable',
                              label: '',
                              options: [{ label: "Best Comparable", value: true }],
                              row: true,
                              onChange: (value) => compToolStore.onBestComparableChange(compFormStores[index].form.fields.propertyId.value, value),
                            })}
                          </Box>
                        </CompPropertyCell>
                      ))}
                    </StyledTableRow>
                    <StyledTableRow>
                      {propertyComps.map((comp, index) => (
                        <CompPropertyCell key={`weight-adjustment-cell-${comp.property_id}`} className={classes.noBorder}>
                          {createPercentageField({
                            label: '',
                            fieldName: 'weightAdjustment',
                            testId: 'weightAdjustment' + comp.property_id,
                            store: compFormStores[index],
                            standaloneLabel: false,
                          })}
                        </CompPropertyCell>
                      ))}
                    </StyledTableRow>
                    <StyledTableRow>
                      {propertyComps.map((comp, index) => (
                        <CompPropertyCell key={`noOfUnits-cell-${comp.property_id}`} className={classes.noBorder}>
                          {createNumberFormatField({
                            label: '',
                            fieldName: 'noOfUnits',
                            testId: 'noOfUnits' + comp.property_id,
                            store: compFormStores[index],
                            standaloneLabel: false,
                          })}
                        </CompPropertyCell>
                      ))}
                    </StyledTableRow>
                    <StyledTableRow>
                      {propertyComps.map((comp, index) => (
                        <CompPropertyCell key={`view-cell-${comp.property_id}`} className={classes.noBorder} style={{ textAlign: "left" }}>
                          {createSelectField({
                            store: compFormStores[index],
                            testId: 'view' + comp.property_id,
                            fieldName: 'view',
                            options: viewOptions,
                            standaloneLabel: true,
                          })}
                        </CompPropertyCell>
                      ))}
                    </StyledTableRow>
                    <StyledTableRow>
                      {propertyComps.map((comp, index) => (
                        <CompPropertyCell key={`comparability-cell-${comp.property_id}`} className={classes.noBorder} style={{ textAlign: "left" }}>
                          {createSelectField({
                            store: compFormStores[index],
                            testId: 'comparability' + comp.property_id,
                            fieldName: 'comparability',
                            options: viewOptions,
                            standaloneLabel: true,
                          })}
                        </CompPropertyCell>
                      ))}
                    </StyledTableRow>
                  </>)}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </Box>
        {loanSelected && (
          <Box>
            <TableContainer>
              <Table style={{ tableLayout: "fixed", width: "100%" }}>
                <TableBody>
                  <StyledTableRow>
                    <StyledTableCell>Comments</StyledTableCell>
                    <SubjectPropertyCell style={{ width: "auto" }}>
                      <TextField
                        type="text"
                        variant="outlined"
                        testId="compareComments"
                        standaloneLabel={false}
                        label=""
                        value={compToolStore.compareComments}
                        onChange={e => compToolStore.setCompareComments(e.target.value)}
                        fullWidth
                        multiline
                        minRows={4}
                      />
                    </SubjectPropertyCell>
                  </StyledTableRow>
                </TableBody>
              </Table>
            </TableContainer>

          </Box>
        )}
      </Box>
      <ComparablesFeedbackModal open={openComparablesFeedbackModal}
        onClose={() => setOpenComparablesFeedbackModal(false)}
        onSaveComplete={handleOnSaveComplete} />

    </Box>
  );
});

interface PropertyHeaderProps {
  property: ComparableProperty;
  title: string;
  onRemove?: () => void;
}

const PropertyHeader = ({ title, property, onRemove }: PropertyHeaderProps) => {
  const classes = useStyles();

  return (<Box
    display="flex"
    flexDirection="column"
    justifyContent="start"
    alignItems="center"
    height="100%"
    gridGap="20px"
  >
    <Box display="flex" justifyContent="center" alignItems="center">
      <Typography
        className={classes.headerTitle}
        variant="body1"
        style={{ fontWeight: "bold" }}
      >
        {title}
      </Typography>
      {property.added_manually_comparable && <HouseWithPlusFilledIcon className={classes.icon} />}
    </Box>

    <Box style={{ maxWidth: "400px", width: "100%" }}>
      <PropertyPhotosGallery propertyId={property.property_id} images={property?.data?.images} height="150px" />
    </Box>
    {onRemove ? (<Button color="primary" variant="outlined" onClick={onRemove}>
      Remove
    </Button>) : (<Box style={{ height: "36px" }}></Box>)}

  </Box>);
}

export default CompareSelection;

const StyledTableRow = withStyles({
  root: {
    height: 80,
  },
})(TableRow);

const StyledTableCell = withStyles(() =>
  createStyles({
    body: ({ withBorder }: any) => ({
      padding: 10,
      border: withBorder ? "1px solid #D9DADC" : "none",
      borderBottom: withBorder ? "" : "none",
      textAlign: "center",
      backgroundColor: "#F4F4F4",
      width: "300px"
    }),
    head: ({ withBorder }: any) => ({
      fontWeight: "bold",
      border: withBorder ? "1px solid #D9DADC" : "none",
      borderBottom: withBorder ? "" : "none",
      textAlign: "center",
      padding: 10,
      letterSpacing: "1px",
      height: "200px",
      backgroundColor: "#F4F4F4",
      width: "300px"
    }),
  })
)(TableCell);

const SubjectPropertyCell = withStyles(() =>
  createStyles({
    body: ({ withBorder }: any) => ({
      padding: 10,
      border: withBorder ? "1px solid #D9DADC" : "none",
      borderBottom: withBorder ? "" : "none",
      textAlign: "center",
      backgroundColor: "#E8ECF1",
      height: "80px",
      width: "300px"
    }),
    head: ({ withBorder }: any) => ({
      fontWeight: "bold",
      border: withBorder ? "1px solid #D9DADC" : "none",
      borderBottom: withBorder ? "" : "none",
      textAlign: "center",
      padding: 10,
      letterSpacing: "1px",
      height: "200px",
      backgroundColor: "#E8ECF1",
      width: "300px"
    }),
  })
)(TableCell);

const CompPropertyCell = withStyles(() =>
  createStyles({
    body: ({ withBorder }: any) => ({
      padding: 10,
      border: withBorder ? "1px solid #D9DADC" : "none",
      borderBottom: withBorder ? "" : "none",
      textAlign: "center",
      backgroundColor: "#FFFFFF",
      height: "80px",
      width: "300px",
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    }),
    head: ({ withBorder }: any) => ({
      fontWeight: "bold",
      border: withBorder ? "1px solid #D9DADC" : "none",
      borderBottom: withBorder ? "" : "none",
      textAlign: "center",
      padding: 10,
      letterSpacing: "1px",
      height: "200px",
      backgroundColor: "#FFFFFF",
      width: "300px",
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    }),
  })
)(TableCell);

const getPropertyTypeOptions = (propertyType: string) => {
  const options = propertyTypeOptions.map(op => ({ label: op.label, value: op.value.toLowerCase() }));

  if (propertyType && !propertyTypeOptions.some(op => op.value.toLowerCase() === propertyType.toLowerCase())) {
    return [{ label: capitalizeOnlyFirstLetter(propertyType), value: propertyType.toLowerCase() }, ...options];
  }
  return options;
};
